import React, { useMemo } from "react";
import { Stack, Text } from "@fluentui/react";

interface RawIndicatorRow {
  sectionName?: string;
  metricName?: string;
  factorLabel?: string;
  value: string | number;
}

// ---- Config ----

const CHANNEL_ORDER = ["Depth", "Surveys", "Comm Calls", "Focus Groups"] as const;
type ChannelKey = typeof CHANNEL_ORDER[number];

const CHANNEL_CONFIG: Record<ChannelKey, { label: string; bg: string; valueColor: string }> = {
  "Depth":        { label: "DEPTH",        bg: "#EBF3FB", valueColor: "#1252A3" },
  "Surveys":      { label: "SURVEYS",      bg: "#EBF7E8", valueColor: "#107C10" },
  "Comm Calls":   { label: "COMM CALLS",   bg: "#FDF8E3", valueColor: "#8A6500" },
  "Focus Groups": { label: "FOCUS GROUPS", bg: "#F3EEF8", valueColor: "#5C2D91" },
};

const SCORING_BINS = [
  { label: "High Engagement", desc: "75–100: Active across multiple channels",    bg: "#ECF6EA", textColor: "#107C10" },
  { label: "Moderate",        desc: "50–75: Some engagement (floor applies)",      bg: "#EAF3FB", textColor: "#1252A3" },
  { label: "Low",             desc: "1–50: Minimal (no floor, TotalCount=0)",      bg: "#FFF9E5", textColor: "#8A6500" },
  { label: "None",            desc: "0: Zero engagement everywhere",               bg: "#FDE8E8", textColor: "#A4262C" },
];

// ---- Helpers ----

const toText = (v: string | number): string => String(v ?? "").trim();

const parseNumber = (v: string | number | undefined): number | undefined => {
  if (v === undefined) return undefined;
  const n = Number(String(v).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : undefined;
};

const formatDecimal = (v: number | undefined, digits = 1): string =>
  v === undefined
    ? "-"
    : v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: digits });

const prettifyFormula = (s: string): string =>
  s.replace(/->/g, "→").replace(/\bx\b/g, "×");

// ---- Data Types & Parser ----

interface ChannelData {
  count?: number;
  normSqrtText: string;
}

interface SummaryLine {
  text: string;
  isFinal: boolean;
  value?: number;
}

interface CommunityEngagementData {
  channels: Partial<Record<ChannelKey, ChannelData>>;
  summaryLines: SummaryLine[];
  totalScore?: number;
}

const buildCommunityData = (rows: RawIndicatorRow[]): CommunityEngagementData => {
  const channels: Partial<Record<ChannelKey, ChannelData>> = {};
  const summaryLines: SummaryLine[] = [];

  // Initialise channel slots
  for (const ch of CHANNEL_ORDER) {
    channels[ch] = { normSqrtText: "" };
  }

  for (const row of rows) {
    const section    = (row.sectionName  || "").trim();
    const metricType = (row.metricName   || "").trim();
    const label      = (row.factorLabel  || "").trim();

    if (metricType === "Factors" && (CHANNEL_ORDER as readonly string[]).includes(section)) {
      const ch = channels[section as ChannelKey]!;
      const lower = label.toLowerCase();
      if (lower.includes("count")) {
        ch.count = parseNumber(row.value);
      } else if (lower.startsWith("norm:")) {
        ch.normSqrtText = prettifyFormula(label);
      }
      continue;
    }

    if (section === "Community Engagement Score" && metricType === "Summary") {
      const isFinal = label.toLowerCase().startsWith("totalcount");
      summaryLines.push({ text: prettifyFormula(label), isFinal, value: parseNumber(row.value) });
    }
  }

  const totalScore = summaryLines.find((l) => l.isFinal)?.value;

  return { channels, summaryLines, totalScore };
};

// ---- Component ----

interface MTPCommunityEngagementRawIndicatorCardsProps {
  rows: RawIndicatorRow[];
  productAbbr: string;
  weight?: string;
  contribution?: string;
}

const MTPCommunityEngagementRawIndicatorCards: React.FC<MTPCommunityEngagementRawIndicatorCardsProps> = ({
  rows, productAbbr, weight, contribution,
}) => {
  const data = useMemo(() => buildCommunityData(rows), [rows]);

  const scoreColor =
    data.totalScore === undefined ? "#605E5C"
    : data.totalScore >= 75 ? "#107C10"
    : data.totalScore >= 50 ? "#1252A3"
    : data.totalScore >= 1  ? "#8A6500"
    : "#A4262C";

  return (
    <Stack
      tokens={{ childrenGap: 16 }}
      styles={{
        root: {
          flex: "0 0 auto",
          maxWidth: 680,
          borderRadius: 10,
          padding: "18px 20px",
          border: "2px solid #1252A3",
          backgroundColor: "#FFFFFF",
        },
      }}
    >
      {/* Header */}
      <Stack horizontal horizontalAlign="space-between" verticalAlign="start">
        <Text
          variant="medium"
          styles={{
            root: {
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: "#243A5E",
            },
          }}
        >
          {productAbbr !== "ALL" ? `${productAbbr} — ` : ""}Community Engagement
        </Text>
        <Stack horizontalAlign="end" tokens={{ childrenGap: 4 }}>
          {weight && (
            <Stack
              styles={{
                root: {
                  backgroundColor: "#1252A3",
                  borderRadius: 20,
                  padding: "3px 14px",
                  alignSelf: "flex-end",
                },
              }}
            >
              <Text variant="small" styles={{ root: { color: "#FFFFFF", fontWeight: 600 } }}>
                Weight: {weight}
              </Text>
            </Stack>
          )}
          {contribution && (
            <Text variant="small" styles={{ root: { color: "#605E5C" } }}>
              Contribution: {contribution}
            </Text>
          )}
        </Stack>
      </Stack>

      {/* Total Score */}
      <Text variant="xxLarge" styles={{ root: { fontWeight: 700, color: scoreColor, lineHeight: "1" } }}>
        {formatDecimal(data.totalScore, 1)}
      </Text>

      {/* Channel Boxes */}
      <Stack horizontal tokens={{ childrenGap: 10 }} wrap>
        {CHANNEL_ORDER.map((ch) => {
          const config  = CHANNEL_CONFIG[ch];
          const chData  = data.channels[ch];
          return (
            <Stack
              key={ch}
              tokens={{ childrenGap: 4 }}
              styles={{
                root: {
                  flex: "1 1 130px",
                  minWidth: 120,
                  backgroundColor: config.bg,
                  borderRadius: 8,
                  padding: "10px 12px",
                  alignItems: "center",
                },
              }}
            >
              <Text
                variant="tiny"
                styles={{
                  root: {
                    textTransform: "uppercase",
                    letterSpacing: "0.07em",
                    fontWeight: 700,
                    color: "#605E5C",
                  },
                }}
              >
                {config.label}
              </Text>
              <Text
                variant="xLarge"
                styles={{ root: { fontWeight: 700, color: config.valueColor, lineHeight: "1.1" } }}
              >
                {chData?.count !== undefined ? String(chData.count) : "-"}
              </Text>
              {chData?.normSqrtText && (
                <Text variant="tiny" styles={{ root: { color: "#605E5C", textAlign: "center" } }}>
                  {chData.normSqrtText}
                </Text>
              )}
            </Stack>
          );
        })}
      </Stack>

      {/* Formula Section */}
      {data.summaryLines.length > 0 && (
        <Stack
          tokens={{ childrenGap: 5 }}
          styles={{
            root: {
              backgroundColor: "#F5F8FF",
              border: "1px solid #D4E3FA",
              borderRadius: 6,
              padding: "10px 14px",
            },
          }}
        >
          {data.summaryLines.map((line, idx) => (
            <Text
              key={idx}
              variant="small"
              styles={{
                root: {
                  fontFamily: "monospace",
                  color: line.isFinal ? scoreColor : "#323130",
                  fontWeight: line.isFinal ? 700 : 400,
                },
              }}
            >
              {line.text}
            </Text>
          ))}
        </Stack>
      )}

      {/* Scoring Bins */}
      <Stack horizontal tokens={{ childrenGap: 8 }} wrap>
        {SCORING_BINS.map((bin) => (
          <Stack
            key={bin.label}
            tokens={{ childrenGap: 3 }}
            styles={{
              root: {
                flex: "1 1 130px",
                minWidth: 120,
                backgroundColor: bin.bg,
                borderRadius: 8,
                padding: "8px 12px",
                alignItems: "center",
              },
            }}
          >
            <Text
              variant="small"
              styles={{ root: { fontWeight: 700, color: bin.textColor, textAlign: "center" } }}
            >
              {bin.label}
            </Text>
            <Text variant="tiny" styles={{ root: { color: bin.textColor, textAlign: "center", opacity: 0.85 } }}>
              {bin.desc}
            </Text>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};

export default MTPCommunityEngagementRawIndicatorCards;
